import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { type PostDto } from '@/features/posts/api'
import { labelPost, mediaUtama } from '@/features/posts/label'
import { PIN_STATUS_LABEL } from '@/features/posts/constants'

interface AttemptDto {
  attemptNo: number
  state: string
  startedAt: number | null
  finishedAt: number | null
  durationMs: number | null
  lastStep: string | null
  errorCode: string | null
  errorMessage: string | null
  screenshotKey: string | null
  resultPinUrl: string | null
}

interface EventDto {
  id: string
  level: string
  code: string
  message: string | null
  createdAt: number
}

export default function HistoryPage() {
  const [tab, setTab] = useState<'posts' | 'events'>('posts')
  const [posts, setPosts] = useState<PostDto[]>([])
  const [events, setEvents] = useState<EventDto[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [attempts, setAttempts] = useState<Record<string, AttemptDto[]>>({})
  const [lightbox, setLightbox] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [pinsRes, eventsRes] = await Promise.all([
        api.get<{ posts: PostDto[] }>('/posts?status=posted,failed,cancelled&limit=100'),
        api.get<{ events: EventDto[] }>('/logs?limit=100'),
      ])
      setPosts(pinsRes.posts)
      setEvents(eventsRes.events)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load history")
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function toggleAttempts(postId: string) {
    if (expanded === postId) {
      setExpanded(null)
      return
    }
    setExpanded(postId)
    if (!attempts[postId]) {
      try {
        const res = await api.get<{ attempts: AttemptDto[] }>(`/logs/attempts/${postId}`)
        setAttempts((a) => ({ ...a, [postId]: res.attempts }))
      } catch {
        /* biar kosong */
      }
    }
  }

  async function retry(post: PostDto) {
    try {
      await api.post(`/posts/${post.id}/retry`)
      toast.success('Post put back into the queue')
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't retry")
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">History</h1>
        <p className="text-sm text-muted-foreground">Published & failed posts, plus the event log</p>
      </header>

      <div className="flex gap-1 rounded-md bg-muted p-1 w-fit">
        {(
          [
            ['posts', `Posts (${posts.length})`],
            ['events', `Event log (${events.length})`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'rounded px-4 py-1.5 text-sm transition-colors',
              tab === id ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'posts' && (
        <div className="space-y-2">
          {posts.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No history yet.
              </CardContent>
            </Card>
          )}
          {posts.map((post) => {
            const status = PIN_STATUS_LABEL[post.status] ?? PIN_STATUS_LABEL.draft
            const list = attempts[post.id]
            return (
              <Card key={post.id}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
                      {mediaUtama(post)?.previewUrl ? (
                        <img
                          src={mediaUtama(post)!.previewUrl!}
                          alt=""
                          className="size-full object-cover"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => void toggleAttempts(post.id)}
                          className="truncate text-left text-sm font-medium hover:underline"
                        >
                          {labelPost(post) || '(no title)'}
                        </button>
                        <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-xs', status.className)}>
                          {status.text}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {post.postedAt
                          ? `published ${new Date(post.postedAt).toLocaleString('en-GB')}`
                          : post.lastErrorMessage || post.lastErrorCode || ''}
                      </p>
                    </div>
                    {post.igPermalink && (
                      <a
                        href={post.igPermalink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex shrink-0 items-center gap-1 text-xs text-foreground hover:underline"
                      >
                        View post <ExternalLink className="size-3" />
                      </a>
                    )}
                    {post.status === 'failed' && (
                      <Button variant="outline" size="sm" onClick={() => void retry(post)}>
                        <RotateCcw />
                        Try again
                      </Button>
                    )}
                  </div>

                  {expanded === post.id && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {!list && <p className="text-xs text-muted-foreground">Loading attempts…</p>}
                      {list?.length === 0 && (
                        <p className="text-xs text-muted-foreground">No attempts recorded yet.</p>
                      )}
                      {list?.map((a) => (
                        <div key={a.attemptNo} className="flex items-center gap-3 text-xs">
                          <span className="w-20 shrink-0 text-muted-foreground">
                            Attempt {a.attemptNo}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 rounded px-1.5 py-0.5',
                              a.state === 'succeeded'
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : a.state === 'running'
                                  ? 'bg-sky-500/15 text-sky-400'
                                  : 'bg-destructive/15 text-destructive',
                            )}
                          >
                            {a.state}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {a.errorCode ? `${a.errorCode}: ${a.errorMessage ?? ''}` : a.lastStep ?? ''}
                            {a.durationMs ? ` · ${Math.round(a.durationMs / 100) / 10}s` : ''}
                          </span>
                          {a.screenshotKey && (
                            <button
                              className="shrink-0 text-foreground hover:underline"
                              onClick={() =>
                                setLightbox(`/api/logs/artifact?key=${encodeURIComponent(a.screenshotKey!)}`)
                              }
                            >
                              Screenshot
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {tab === 'events' && (
        <Card>
          <CardContent className="divide-y p-0">
            {events.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <span
                  className={cn(
                    'w-12 shrink-0 font-medium',
                    e.level === 'error'
                      ? 'text-destructive'
                      : e.level === 'warn'
                        ? 'text-amber-500'
                        : 'text-muted-foreground',
                  )}
                >
                  {e.level}
                </span>
                <span className="w-44 shrink-0 truncate text-muted-foreground">{e.code}</span>
                <span className="min-w-0 flex-1 truncate">{e.message}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {new Date(e.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {lightbox && (
        <button
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-8"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="Failure screenshot" className="max-h-full max-w-full rounded-lg" />
        </button>
      )}
    </div>
  )
}
